// =============================================================================
// Event Timeline — chronological order event list (portal)
// =============================================================================

import { Badge } from '@tkpod/ui';
import { getMerchantOrderStatusLabel } from '@tkpod/shared';

interface OrderEvent {
  id: string;
  type: string;
  data: Record<string, unknown> | null;
  userId?: string | null;
  userName: string | null;
  createdAt: string;
}

interface EventTimelineProps {
  events: OrderEvent[];
}

const EVENT_LABELS: Record<string, string> = {
  ORDER_CREATED: 'Order Created',
  STATUS_CHANGED: 'Status Changed',
  EXPORT_STARTED: 'Export Started',
  EXPORT_COMPLETED: 'Export Completed',
  EXPORT_FAILED: 'Export Failed',
  EXPORT_REGENERATED: 'Exports Regenerated',
  DESIGN_EDITED: 'Design Edited',
  ARTWORK_REPLACED: 'File replaced',
  PROVIDER_OVERRIDDEN: 'Provider Overridden',
  ON_HOLD: 'Put on Hold',
  HOLD_RELEASED: 'Released from Hold',
  CANCELLED: 'Cancelled',
  CANCEL_REQUESTED: 'Cancel Requested',
  NOTE_ADDED: 'Note Added',
  NOTE_UPDATED: 'Note Updated',
  TAG_ADDED: 'Tag Added',
  TAG_REMOVED: 'Tag Removed',
};

function relativeTime(iso: string): string {
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diff = now - then;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function getDateGroupLabel(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const yesterday = today - 86_400_000;
  const eventDay = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  if (eventDay === today) return 'Today';
  if (eventDay === yesterday) return 'Yesterday';
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

/**
 * Internal pipeline faults the merchant can neither diagnose nor fix —
 * hidden from the merchant timeline. The status badge folds these into
 * "Order Processing" (see ORDER_STATUS_MERCHANT_LABELS); showing the raw
 * failure event here would undo that. The admin timeline still shows them.
 */
const MERCHANT_HIDDEN_EVENT_TYPES = new Set(['EXPORT_FAILED']);

export function EventTimeline({ events: allEvents }: EventTimelineProps) {
  const events = allEvents.filter(
    (e) => !MERCHANT_HIDDEN_EVENT_TYPES.has(e.type),
  );
  if (events.length === 0) {
    return (
      <p className="py-3 text-center text-xs text-text-tertiary">
        No events recorded.
      </p>
    );
  }

  const groups = new Map<string, OrderEvent[]>();
  for (const event of events) {
    const label = getDateGroupLabel(event.createdAt);
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label)!.push(event);
  }

  return (
    <div className="space-y-4">
      {Array.from(groups.entries()).map(([dateLabel, groupEvents]) => (
        <div key={dateLabel}>
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">
            {dateLabel}
          </p>
          <div className="relative pl-4">
            <div className="absolute left-[5px] top-1 bottom-1 w-px bg-border" />
            <div className="space-y-3">
              {groupEvents.map((event) => {
                const d = event.data as Record<string, unknown> | null;
                const isNote = event.type === 'NOTE_ADDED' || event.type === 'NOTE_UPDATED';
                const isProviderOverride = event.type === 'PROVIDER_OVERRIDDEN';
                return (
                  <div key={event.id} className="relative">
                    <div className={`absolute -left-4 top-[5px] h-[7px] w-[7px] rounded-full border-2 ${isProviderOverride ? 'border-orange-400 bg-orange-100' : 'border-border bg-surface'}`} />
                    <div className="space-y-0.5">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="text-xs font-medium text-text-primary">
                          {isNote && d?.body
                            ? 'Note added.'
                            : EVENT_LABELS[event.type] ?? event.type.replace(/_/g, ' ')}
                        </span>
                        <span className="text-[10px] text-text-tertiary" title={event.createdAt}>
                          {relativeTime(event.createdAt)}
                        </span>
                      </div>

                      {isProviderOverride && d && (() => {
                        const fromName = (d.fromProviderName as string) || null;
                        const toName = (d.toProviderName as string) || (d.providerName as string) || null;
                        return (
                          <div className="mt-1.5 rounded-lg border border-orange-400/30 bg-orange-50/50 dark:bg-orange-500/5 px-3 py-2.5 space-y-2">
                            {!d.itemLevel && (
                              fromName && toName ? (
                                <div className="flex items-center gap-2 text-xs">
                                  <span className="inline-flex items-center gap-1 rounded-md bg-surface px-2 py-0.5 border border-border font-medium text-text-primary">
                                    {fromName}
                                  </span>
                                  <svg className="h-4 w-4 text-orange-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                                  </svg>
                                  <span className="inline-flex items-center gap-1 rounded-md bg-orange-100 dark:bg-orange-500/15 px-2 py-0.5 border border-orange-300/50 font-semibold text-orange-700 dark:text-orange-400">
                                    {toName}
                                  </span>
                                </div>
                              ) : (
                                <p className="text-xs text-text-primary">
                                  Assigned to{' '}
                                  <span className="inline-flex items-center gap-1 rounded-md bg-orange-100 dark:bg-orange-500/15 px-2 py-0.5 border border-orange-300/50 font-semibold text-orange-700 dark:text-orange-400">
                                    {toName || '—'}
                                  </span>
                                </p>
                              )
                            )}
                            {d.itemLevel === true && Array.isArray(d.assignments) && (
                              <div className="space-y-2">
                                <p className="text-[10px] font-semibold uppercase tracking-wider text-orange-600">
                                  {(d.assignments as Array<Record<string, string>>).length} item{(d.assignments as Array<Record<string, string>>).length !== 1 ? 's' : ''} reassigned
                                </p>
                                {(d.assignments as Array<Record<string, string>>).map((a, i) => (
                                  <div key={i} className="rounded-md border border-border/50 bg-surface/50 px-2.5 py-1.5 space-y-1">
                                    <p className="text-xs font-medium text-text-primary leading-snug truncate">
                                      {a.itemTitle || 'Unknown item'}
                                      {a.itemSku && (
                                        <span className="ml-1.5 font-mono text-[10px] text-text-tertiary bg-surface-secondary px-1 py-px rounded">
                                          {a.itemSku}
                                        </span>
                                      )}
                                    </p>
                                    <div className="flex items-center gap-2 text-xs">
                                      {a.fromProviderName ? (
                                        <>
                                          <span className="inline-flex items-center rounded-md bg-surface px-2 py-0.5 border border-border font-medium text-text-primary text-[11px]">
                                            {a.fromProviderName}
                                          </span>
                                          <svg className="h-3.5 w-3.5 text-orange-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                                          </svg>
                                        </>
                                      ) : (
                                        <span className="text-[11px] text-text-tertiary">Assigned to</span>
                                      )}
                                      <span className="inline-flex items-center rounded-md bg-orange-100 dark:bg-orange-500/15 px-2 py-0.5 border border-orange-300/50 font-semibold text-orange-700 dark:text-orange-400 text-[11px]">
                                        {a.providerName}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            {d.reason != null && (
                              <p className="text-[11px] text-text-secondary">
                                <span className="font-medium text-text-tertiary">Reason:</span> {String(d.reason)}
                              </p>
                            )}
                            {event.userName && (
                              <p className="text-[10px] text-text-tertiary">by {event.userName}</p>
                            )}
                          </div>
                        );
                      })()}

                      {d && !isNote && !isProviderOverride && (
                        <div className="text-[11px] leading-relaxed text-text-secondary space-y-1">
                          {d.from != null && d.to != null && (() => {
                            // Merchant surface: show merchant-facing status
                            // labels (several internal statuses collapse to
                            // "Order Processing" — render a single badge when
                            // both sides read the same, instead of
                            // "Order Processing → Order Processing").
                            const fromLabel = getMerchantOrderStatusLabel(String(d.from));
                            const toLabel = getMerchantOrderStatusLabel(String(d.to));
                            return (
                              <span className="flex items-center gap-1">
                                {fromLabel !== toLabel && (
                                  <>
                                    <Badge variant="default" size="sm">
                                      {fromLabel}
                                    </Badge>
                                    <span className="text-text-tertiary">→</span>
                                  </>
                                )}
                                <Badge variant="default" size="sm">
                                  {toLabel}
                                </Badge>
                              </span>
                            );
                          })()}
                          {d.reason != null && <p className="text-text-tertiary">Reason: {String(d.reason)}</p>}
                          {d.providerName != null && <p>Provider: <span className="font-medium">{String(d.providerName)}</span></p>}
                          {d.body != null && (
                            <p className="italic text-text-tertiary">&quot;{String(d.body)}&quot;</p>
                          )}
                          {d.tag != null && (
                            <span className="inline-block rounded-full border border-border bg-surface-secondary px-1.5 py-px text-[10px]">
                              {String(d.tag)}
                            </span>
                          )}
                        </div>
                      )}
                      {isNote && d?.body != null && (
                        <p className="text-[11px] italic text-text-secondary">&quot;{String(d.body)}&quot;</p>
                      )}
                      {event.userName && !isProviderOverride && (
                        <p className="text-[10px] text-text-tertiary">by {event.userName}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
